import DownChevron from "@client/icons/DownChevron";
import { Proposal } from "@engine/round/aggregate";
import * as Sentry from "@sentry/react";
import { useLocalStorage } from "@uidotdev/usehooks";
import clsx from "clsx";
import { useMemo } from "react";
import { FilteredProposalForm } from "server/routes/getProposal";
import {
  INITIAL_SECTION_ID,
  buildSections,
  sectionGroupMapping,
} from "./sectionUtils";
import { UntypedFieldResponsesInfo } from "./UntypedFieldResponsesInfo";

export function ProposalInfoPage({
  proposal,
  filteredProposalForm,
}: {
  proposal: Proposal;
  filteredProposalForm: FilteredProposalForm;
}) {
  const [collapsedSections, setCollapsedSections] = useLocalStorage<
    Record<string, boolean>
  >("sectionCollapseState", {});

  // Build sections to get all section IDs
  const sectionCollapsedState = useMemo(
    () =>
      buildSections({
        filteredProposalForm,
        longformLink: proposal.typedFields.longformLink,
      }),
    [filteredProposalForm, proposal.typedFields.longformLink],
  );

  // Create a map from section header title to section ID
  const sectionTitleToId = useMemo(() => {
    return sectionCollapsedState.reduce(
      (acc, section) => {
        if (section.header) {
          acc[section.header.title] = section.id;
        }
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [sectionCollapsedState]);

  const toggleSectionVisibility = (sectionId: string) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const toggleGroupVisibility = (groupHeaderTitle: string) => {
    const groupHeaderId = sectionTitleToId[groupHeaderTitle];
    if (!groupHeaderId) {
      // Should not happen
      Sentry.captureMessage(
        `Section without a known group header title: ${groupHeaderTitle}`,
        "warning",
      );
      return;
    }

    const newCollapsedState = !collapsedSections[groupHeaderId];
    const subHeaderTitles = sectionGroupMapping[groupHeaderTitle] || [];

    const groupUpdates: Record<string, boolean> = {
      [groupHeaderId]: newCollapsedState,
    };

    subHeaderTitles.forEach((subTitle) => {
      const subId = sectionTitleToId[subTitle];
      if (subId) {
        groupUpdates[subId] = newCollapsedState;
      }
    });

    setCollapsedSections((prev) => ({ ...prev, ...groupUpdates }));
  };

  const isAllSectionsCollapsed = Object.values(collapsedSections).every(
    (collapsed) => collapsed,
  );

  const toggleAllSections = () => {
    // Create an object with all sections set to the new collapsed state
    const allSectionsState = sectionCollapsedState.reduce(
      (acc, section) => {
        acc[section.id] = !isAllSectionsCollapsed;
        return acc;
      },
      { [INITIAL_SECTION_ID]: !isAllSectionsCollapsed } as Record<
        string,
        boolean
      >,
    );

    setCollapsedSections(allSectionsState);
  };

  const isAppInfoCollapsed = collapsedSections[INITIAL_SECTION_ID];

  return (
    <div className="flex flex-col">
      <div className="mb-8 mt-6">
        <span
          className="inline-block w-30 underline underline-offset-4 cursor-pointer light:text-blue-600 light:hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          onClick={toggleAllSections}
        >
          {isAllSectionsCollapsed ? "Expand All" : "Collapse All"}
        </span>
      </div>

      <div
        className="flex items-center mb-4 cursor-pointer"
        onClick={() => toggleSectionVisibility(INITIAL_SECTION_ID)}
      >
        <div
          className={clsx(
            "shrink-0 w-5 h-5 fill-stone-700 dark:fill-white transform text-stone-200",
            isAppInfoCollapsed ? "-rotate-90" : "",
          )}
        >
          <DownChevron />
        </div>
        <h3 className="m-0">Application Info</h3>
      </div>
      <UntypedFieldResponsesInfo
        fieldResponses={proposal.fieldResponses}
        entityType={proposal.entityType}
        requestsMatchingPledge={proposal.requestsMatchingPledge}
        allCollapsedSections={collapsedSections}
        toggleSectionVisibility={toggleSectionVisibility}
        isAppInfoCollapsed={isAppInfoCollapsed}
        sections={sectionCollapsedState}
        toggleGroupVisibility={toggleGroupVisibility}
      />
    </div>
  );
}
